import React, { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { ColorOptionLabel, MiIcon } from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { mdiClose } from "@mdi/js";
import {
  Box,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { useCalendarContext } from "../../../../providers";

export const OrderTypeFilter = memo(
  ({ selectedTemplates, setSelectedTemplates }: OrderTypeFilterProps) => {
    const { classes, theme } = useOrderTypeFilterStyles();
    const { calendarSet } = useCalendarContext();

    const orderTemplateOptions =
      calendarSet?.config.workTemplateBases.map((template) => ({
        label: template.name,
        value: template.id || template.name,
        color: template.color || theme.palette.grey.primary,
      })) ?? [];

    const handleTemplateChange = (event: {
      target: { value: string | string[] };
    }) => {
      const { value } = event.target;
      if (Array.isArray(value) && value.includes("all-orders")) {
        setSelectedTemplates(
          orderTemplateOptions.map((option) => option.value)
        );
      } else {
        setSelectedTemplates(
          typeof value === "string" ? value.split(",") : value
        );
      }
    };

    const handleClearSelection = () => {
      setSelectedTemplates([]);
    };

    return (
      <Box className={classes.boardSelectionContainer}>
        <FormControl className={classes.boardSelector}>
          <Select
            multiple
            size="small"
            displayEmpty
            className={classes.boardSelector}
            value={selectedTemplates}
            onChange={handleTemplateChange}
            input={<OutlinedInput />}
            renderValue={(selected) => (
              <Box className={classes.selectedValueContainer}>
                {selected.length === 0 ? (
                  <Typography
                    variant={TYPOGRAPHY_VARIANTS.BODY2}
                    color={theme.palette.text.secondary}
                  >
                    Select Order Type
                  </Typography>
                ) : (
                  <>
                    <Box className={classes.selectedLabelsContainer}>
                      {selected.length === orderTemplateOptions.length ? (
                        <Typography
                          variant={TYPOGRAPHY_VARIANTS.BODY2}
                          color={theme.palette.primary.main}
                          fontWeight={theme.typography.fontWeightBold}
                        >
                          All Orders
                        </Typography>
                      ) : (
                        selected.map((value, index) => {
                          const option = orderTemplateOptions.find(
                            (opt) => opt.value === value
                          );
                          return (
                            <React.Fragment key={value}>
                              <ColorOptionLabel
                                color={
                                  option?.color || theme.palette.grey.primary
                                }
                                label={option?.label || value}
                              />
                              {index < selected.length - 1 && (
                                <Typography
                                  variant={TYPOGRAPHY_VARIANTS.BODY2}
                                  className={classes.separator}
                                >
                                  ,
                                </Typography>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </Box>
                    <Box
                      className={classes.clearButton}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                      onMouseDown={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <MiIcon
                        path={mdiClose}
                        size={0.8}
                        color={theme.palette.text.secondary}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}
          >
            {orderTemplateOptions.map(({ color, label, value }) => (
              <MenuItem key={value} value={value}>
                <ColorOptionLabel color={color} label={label} />
              </MenuItem>
            ))}
            <MenuItem value="all-orders">
              <Typography
                variant={TYPOGRAPHY_VARIANTS.BODY1}
                color={theme.palette.primary.main}
                fontWeight={theme.typography.fontWeightBold}
              >
                All Orders
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }
);

const useOrderTypeFilterStyles = makeStyles()((theme) => ({
  boardSelectionContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(3, 0, 0, 3),
    gap: theme.spacing(1),
  },
  selectedLabelsContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flex: 1,
    paddingRight: 0,
  },
  separator: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  selectedValueContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(0.5),
  },
  clearButton: {
    opacity: 0.7,
    "&:hover": { opacity: 1 },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    pointerEvents: "auto",
  },
  boardSelector: {
    minWidth: 200,
    "& .MuiSelect-select": {
      display: "flex",
      padding: theme.spacing(1, 2),
      alignItems: "center",
      borderRadius: theme.spacing(0.5),
      gap: theme.spacing(0.5),
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.common.black,
    },
    "& .MuiInputBase-root": {
      borderRadius: theme.spacing(0.5),
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "black ",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "black ",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "black ",
      borderWidth: "2px ",
    },
  },
}));

interface OrderTypeFilterProps {
  selectedTemplates: string[];
  setSelectedTemplates: (value: string[]) => void;
}
